import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, CardBody, Label, Input } from "reactstrap";
import Select from "react-select";
import { useNavigate } from "react-router-dom";

import apiClient from "../../axiosInstance";
import showAlert from "../../components/Common/ShowAlert";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const VehicleRegistrations = () => {
  const [breadcrumbItems] = useState([
    { title: "Daryeel", link: "/" },
    { title: "Vehicle Registration", link: "#" },
  ]);
  const navigate = useNavigate();

  // Options state
  const [owners, setOwners] = useState([]);
  const [branches, setBranches] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [purposeOptions, setPurposeOptions] = useState([
    { label: "Select Purpose", value: "", fee: "" },
  ]);

  const ownerOptions = [
    { label: "Select Owner", value: "" },
    ...owners.map((owner) => ({
      label: owner.full_name + " " + (owner.phone_number || ""),
      value: owner.id,
    })),
  ];

  const branchOptions = [
    { label: "Select Branch", value: "" },
    ...branches.map((branch) => ({
      label: branch.name,
      value: branch.id,
    })),
  ];

  const vehicleOptions = [
    { label: "Select Vehicle", value: "" },
    ...vehicles.map((vehicle) => ({
      label: vehicle.license_plate,
      value: vehicle.vehicle_id || vehicle.id,
    })),
  ];

  const customStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: "white",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#007bff"
        : state.isFocused
        ? "#f0f0f0"
        : "white",
      color: state.isSelected ? "white" : "black",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  useEffect(() => {
    const fetchOwners = async () => {
      try {
        const res = await apiClient.get("/owners/getAllowners");
        setOwners(res.data.response || []);
      } catch (err) {
        console.log(err);
      }
    };
    const fetchBranches = async () => {
      try {
        const res = await apiClient.get("/branches/getbranches");
        setBranches(res.data.response);
      } catch (err) {
        console.log(err);
      }
    };
    const fetchVehicles = async () => {
      try {
        const res = await apiClient.get("/vehicles/get-unregistered-vehicles");
        setVehicles(res.data || []);
      } catch (err) {
        console.log(err);
      }
    };
    const fetchPurposes = async () => {
      try {
        const res = await apiClient.get("/fees/registration-fees");
        const activePurposes = (res.data.response || []).filter(
          (fee) => fee.status === "Active"
        );
        setPurposeOptions([
          { label: "Select Purpose", value: "", fee: "" },
          ...activePurposes.map((fee) => ({
            label: fee.purpose,
            value: fee.id,
            fee: fee.fee,
          })),
        ]);
      } catch (err) {
        console.log(err);
      }
    };
    fetchOwners();
    fetchBranches();
    fetchVehicles();
    fetchPurposes();
  }, []);

  // Form state
  const [formData, setFormData] = useState({
    vehicle_id: "",
    owner_id: "",
    branch_id: "",
    registration_date: "",
    odometer_reading: "",
    purpose: "",
    registration_fee: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleReset = () => {
    setFormData({
      vehicle_id: "",
      owner_id: "",
      branch_id: "",
      registration_date: "",
      odometer_reading: "",
      purpose: "",
      registration_fee: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const response = await apiClient.post(
      "/registrations/vehicle-registrations",
      formData
    );
    await showAlert({
      title: "Vehicle Registration!",
      text: response.data.message,
      icon: response.data.status,
      goBack: false,
      timer: 5000,
    });
    if (response.data.status === "success") {
      handleReset();
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Vehicle Registration"
          breadcrumbItems={breadcrumbItems}
        />
        <form onSubmit={handleSubmit}>
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <h4 className="card-title d-flex justify-content-between align-items-center">
                    Vehicle Registration Form
                    <i
                      className="ri-arrow-left-line"
                      style={{ fontSize: "1.25rem", cursor: "pointer" }}
                      onClick={() => navigate(-1)}
                    ></i>
                  </h4>
                  <hr className="bg-primary" />
                  <Row>
                    <Col lg="3" className="mt-3">
                      <Label className="form-label">Vehicle</Label>
                      <Select
                        id="vehicle_id"
                        name="vehicle_id"
                        value={vehicleOptions.find(
                          (option) => option.value === formData.vehicle_id
                        )}
                        onChange={(selected) =>
                          setFormData({
                            ...formData,
                            vehicle_id: selected ? selected.value : "",
                          })
                        }
                        options={vehicleOptions}
                        styles={customStyles}
                        classNamePrefix="select2-selection form-control"
                        required
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </Col>
                    <Col lg="6" className="mt-3">
                      <Label className="form-label">Owner</Label>
                      <Select
                        id="owner_id"
                        name="owner_id"
                        value={ownerOptions.find(
                          (option) => option.value === formData.owner_id
                        )}
                        onChange={(selected) =>
                          setFormData({
                            ...formData,
                            owner_id: selected ? selected.value : "",
                          })
                        }
                        options={ownerOptions}
                        styles={customStyles}
                        classNamePrefix="select2-selection form-control"
                        required
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </Col>
                    <Col lg="3" className="mt-3">
                      <Label className="form-label">Branch</Label>
                      <Select
                        id="branch_id"
                        name="branch_id"
                        value={branchOptions.find(
                          (option) => option.value === formData.branch_id
                        )}
                        onChange={(selected) =>
                          setFormData({
                            ...formData,
                            branch_id: selected ? selected.value : "",
                          })
                        }
                        options={branchOptions}
                        styles={customStyles}
                        classNamePrefix="select2-selection form-control"
                        required
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col lg="3" className="mt-3">
                      <Label className="form-label">Registration Date</Label>
                      <Input
                        type="date"
                        id="registration_date"
                        name="registration_date"
                        value={formData.registration_date}
                        onChange={handleInputChange}
                        required
                      />
                    </Col>
                    <Col lg="3" className="mt-3">
                      <Label className="form-label">Odometer Reading</Label>
                      <Input
                        type="number"
                        id="odometer_reading"
                        name="odometer_reading"
                        placeholder="Enter Odometer Reading"
                        value={formData.odometer_reading}
                        onChange={handleInputChange}
                        required
                      />
                    </Col>
                    <Col lg="3" className="mt-3">
                      <Label className="form-label">Purpose</Label>
                      <Select
                        id="purpose"
                        name="purpose"
                        value={purposeOptions.find(
                          (option) => option.value === formData.purpose
                        )}
                        onChange={(selected) =>
                          setFormData({
                            ...formData,
                            purpose: selected ? selected.value : "",
                            registration_fee: selected ? selected.fee || "" : "",
                          })
                        }
                        options={purposeOptions}
                        styles={customStyles}
                        classNamePrefix="select2-selection form-control"
                        required
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </Col>
                    <Col lg="3" className="mt-3">
                      <Label className="form-label">Amount</Label>
                      <Input
                        type="number"
                        id="registration_fee"
                        name="registration_fee"
                        placeholder="Amount"
                        value={formData.registration_fee}
                        onChange={handleInputChange}
                        required
                      />
                    </Col>
                  </Row>
                  <div className="row d-flex justify-content-end">
                    <div className="col-md-2 col-6">
                      <button
                        type="button"
                        className="btn btn-secondary mt-4 w-100"
                        onClick={handleReset}
                      >
                        Reset
                      </button>
                    </div>
                    <div className="col-md-2 col-6">
                      <button
                        type="submit"
                        className="btn btn-success mt-4 w-100"
                      >
                        Submit
                      </button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </form>
      </Container>
    </div>
  );
};

export default VehicleRegistrations;
